import type { Puzzle } from "./types";

const DAY_MS = 86_400_000;
const DAILY_EPOCH = Date.UTC(2026, 0, 1);

export function utcDateKey(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

const EPOCH_WEEKDAY = new Date(DAILY_EPOCH).getUTCDay();

function daysSinceEpoch(dateKey: string): number {
  const timestamp = Date.parse(`${dateKey}T00:00:00.000Z`);
  if (!Number.isFinite(timestamp)) throw new Error("A valid UTC date is required.");
  return Math.floor((timestamp - DAILY_EPOCH) / DAY_MS);
}

/**
 * How many days of this weekday's own slot have been published before `day`.
 *
 * Indexing on the raw day number looks equivalent and is not: each slot only gets
 * every 7th day, so a Sunday index advances by 7 at a time, and `7 % poolSize`
 * cycles through just `poolSize / gcd(7, poolSize)` entries. With 56 Sunday
 * pictures that is eight of them, forever, no matter how many more get drawn.
 * Counting the slot's own turns makes the pool size mean what it looks like.
 */
function slotOrdinal(day: number, belongs: (weekday: number) => boolean): number {
  const weeks = Math.floor(day / 7);
  let perWeek = 0;
  for (let offset = 0; offset < 7; offset += 1) {
    if (belongs((EPOCH_WEEKDAY + offset) % 7)) perWeek += 1;
  }
  let ordinal = weeks * perWeek;
  for (let offset = 0; offset < day - weeks * 7; offset += 1) {
    if (belongs((EPOCH_WEEKDAY + offset) % 7)) ordinal += 1;
  }
  return ordinal;
}

export function dailyPuzzleIndex(dateKey: string, puzzleCount: number): number {
  if (puzzleCount < 1) throw new Error("A non-empty puzzle catalog is required.");
  const day = daysSinceEpoch(dateKey);
  const weekday = new Date(`${dateKey}T00:00:00.000Z`).getUTCDay();
  const belongs = weekday === 0
    ? (d: number) => d === 0
    : weekday === 6
      ? (d: number) => d === 6
      : (d: number) => d !== 0 && d !== 6;
  const ordinal = slotOrdinal(day, belongs);
  return ((ordinal % puzzleCount) + puzzleCount) % puzzleCount;
}

/**
 * Letters and digits are in the catalogue so the alphabet is complete and playable,
 * but they are the one theme whose reveal is not a picture. The daily puzzle is the
 * one slot where the reveal has to land, so it draws from pictures only.
 */
export function isDailyEligible(puzzle: Puzzle): boolean {
  // The daily schedule promises a size — Sundays 5×5, weekdays 10×10, Saturdays
  // 15×15 — so the grander sizes stay free-play even though they share the
  // difficulty label. A surprise 25×25 Saturday would break the "fifteen quiet
  // minutes" contract the streak is built on.
  return puzzle.theme !== "glyphs" && puzzle.size <= 15;
}

export function dailyDifficultyFor(dateKey: string): Puzzle["difficulty"] {
  const weekday = new Date(`${dateKey}T00:00:00.000Z`).getUTCDay();
  if (weekday === 0) return "easy";
  if (weekday === 6) return "hard";
  return "medium";
}

export function dailyDifficultyLabel(dateKey: string): string {
  const difficulty = dailyDifficultyFor(dateKey);
  return difficulty === "easy" ? "Easy" : difficulty === "hard" ? "Hard" : "Medium";
}

export function dailyPuzzle(puzzles: readonly Puzzle[], dateKey = utcDateKey()): Puzzle {
  const difficulty = dailyDifficultyFor(dateKey);
  const dailyPool = puzzles.filter((puzzle) => puzzle.difficulty === difficulty && isDailyEligible(puzzle));
  const puzzle = dailyPool[dailyPuzzleIndex(dateKey, dailyPool.length)];
  if (!puzzle) throw new Error("Daily puzzle is unavailable.");
  return puzzle;
}

export function nextUtcMidnight(date = new Date()): string {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1)).toISOString();
}

/**
 * Seconds until the daily grid rolls over.
 *
 * Anything that says "today" — the dated archive page's eyebrow, the sitemap's list of
 * published dates — stops being true at that instant and not before, so it is the only
 * sensible max-age for those responses. A fixed hour either serves a stale page past
 * midnight or throws away good cache all day.
 */
export function secondsUntilUtcMidnight(date = new Date()): number {
  return Math.max(1, Math.ceil((Date.parse(nextUtcMidnight(date)) - date.getTime()) / 1000));
}
